import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { Option } from '@mui/base/Option';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ReCAPTCHA from 'react-google-recaptcha';
import Stack from '@mui/material/Stack';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';
import useAuth from 'hooks/useAuth';
import useScriptRef from 'hooks/useScriptRef';
import { strengthColor, strengthIndicator } from 'utils/password-strength';

// assets
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { StringColorProps } from 'types';
import SpinnerLoader from 'ui-component/Spinner';

import { styled, Theme } from '@mui/material/styles';
const CAPTCHA = import.meta.env.VITE_APP_CAPTHA;

interface Forgotpassword {
    theme: Theme;
}

const Forgotpassword = styled(Stack)(({ theme }: Forgotpassword) => ({
    '.MuiFormControl-root': { marginBottom: '10px' },
    'p.Mui-error': {
        position: 'absolute',
        bottom: '-20px',
        left: '-14px',
        [theme.breakpoints.down(380)]: {
            fontSize: '10px'
        }
    }
}));

const countryCodes =
    //['+1', '+44', '+91']; // Add all the country codes you need
    [
        // {
        //     label: 'India',
        //     src: 'https://flagicons.lipis.dev/flags/4x3/in.svg',
        //     value: '+91',
        //     country_code: '+91'
        // },
        {
            label: 'Philippines',
            src: 'https://flagicons.lipis.dev/flags/4x3/ph.svg',
            value: '+63',
            country_code: '+63'
        }
    ];
type LoginType = 'email' | 'phone';
const phone = 'phone';
const email = 'email';

// ===========================|| FIREBASE - REGISTER ||=========================== //

const ForgotPasswordForm = ({
    handleStepChange,
    getRegisterData,
    ...others
}: {
    handleStepChange: Function;
    getRegisterData: Function;
    [key: string]: any;
}) => {
    const theme = useTheme();
    const scriptedRef = useScriptRef();

    const [showPassword, setShowPassword] = React.useState(false);
    const [checked, setChecked] = React.useState(true);
    const [strength, setStrength] = React.useState(0);
    const [level, setLevel] = React.useState<StringColorProps>();

    const [captchaMessage, setCaptchaMessage] = useState('');
    const [isCaptchaDone, setIsCaptchaDone] = useState(false);
    // Set the initial captchaText state to a random 4-digit number

    const [userCaptcha, setUserCaptcha] = useState<string | null>('');
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const { forgotPasswordOtp } = useAuth();
    const [loginMode, setLoginMode] = useState<LoginType>(phone);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.SyntheticEvent) => {
        event.preventDefault();
    };

    const changePassword = (value: string) => {
        const temp = strengthIndicator(value);
        setStrength(temp);
        setLevel(strengthColor(temp));
    };

    const handleCaptcha = (token: string | null) => {
        setIsCaptchaDone(true);
        setUserCaptcha(token);
        setCaptchaMessage('');
    };

    useEffect(() => {
        changePassword('123456');
        //handleStepChange('verification');
    }, []);

    let schema =
        loginMode === phone
            ? {
                  phone: Yup.string()
                      .max(13)
                      .matches(/^[0-9+]*$/, 'Phone number can only contain numbers')
                      .required('Mobile is required'),
                  password: Yup.string().max(255).min(6, 'Password must be at least 6 digits').required('Password is required'),
                  passwordConfirmation: Yup.string()
                      .oneOf([Yup.ref('password'), ''], 'Passwords must match')
                      .required('Confirm Password is required'),
                  email: Yup.string().notRequired()
              }
            : {
                  email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
                  password: Yup.string().max(255).required('Password is required'),
                  passwordConfirmation: Yup.string()
                      .oneOf([Yup.ref('password'), ''], 'Passwords must match')
                      .required('Confirm Password is required')
              };

    return (
        <>
            {/* <Grid container direction="column" justifyContent="center" spacing={2}>
                <Grid item xs={12} container alignItems="center" justifyContent="center">
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1">Sign up with {loginMode === phone ? 'mobile number' : 'email address'}</Typography>
                    </Box>
                </Grid>
            </Grid> */}
            <Forgotpassword>
                <Formik
                    initialValues={{
                        email: '',
                        phone: '',
                        password: '',
                        passwordConfirmation: '',
                        submit: null,
                        countrycode: '+63',
                        token: ''
                    }}
                    validationSchema={Yup.object().shape(schema)}
                    onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                        if (CAPTCHA === 'true') {
                            setCaptchaMessage('');
                            if (!isCaptchaDone) {
                                setCaptchaMessage('Please verify captcha');
                                return;
                            }
                        }
                        const phoneWithCode = `${values.countrycode + values.phone}`;
                        try {
                            const response: any = await forgotPasswordOtp(phoneWithCode, values.email, values.password, userCaptcha);
                            if (recaptchaRef.current) recaptchaRef.current.reset();
                            setCaptchaMessage('');
                            setUserCaptcha('');
                            setIsCaptchaDone(false);
                            const result = response;
                            if (result?.token !== undefined) {
                                handleStepChange('verification');
                                values.token = result.token;
                                getRegisterData(values);
                            } else {
                                setStatus({ success: false });
                                setErrors({ submit: result.error });
                                setSubmitting(false);
                            }
                        } catch (err: any) {
                            if (recaptchaRef.current) recaptchaRef.current.reset();
                            setCaptchaMessage('');
                            setUserCaptcha('');
                            setIsCaptchaDone(false);

                            console.error(err);

                            setStatus({ success: false });
                            setErrors({ submit: err.response.data.message });
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit} {...others}>
                            {loginMode === phone && (
                                <>
                                    <Grid sx={{ gap: '10px', display: 'flex' }}>
                                        <FormControl
                                            fullWidth
                                            error={Boolean(touched.countrycode && errors.countrycode)}
                                            sx={{
                                                ...theme.typography.customInput,
                                                width: '30%',
                                                [theme.breakpoints.down(600)]: {
                                                    width: '30%'
                                                }
                                            }}
                                        >
                                            <InputLabel
                                                sx={{
                                                    position: 'absolute',
                                                    top: '0px !important'
                                                    // color: '#121926 !important'
                                                }}
                                                id="age-select"
                                            >
                                                Country Code
                                            </InputLabel>
                                            <Select
                                                labelId="outlined-adornment-countrycode-register"
                                                id="outlined-adornment-countrycode-register"
                                                value={values.countrycode}
                                                name="countrycode"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                label="Country Code"
                                                sx={{ padding: '0px', fontSize: '12px', fontWeight: '700' }}
                                            >
                                                {countryCodes.map((country: any) => (
                                                    <MenuItem
                                                        sx={{
                                                            [theme.breakpoints.down(600)]: {
                                                                padding: '0px'
                                                            }
                                                        }}
                                                        key={country.country_code}
                                                        value={country.country_code}
                                                    >
                                                        <img
                                                            loading="lazy"
                                                            width={15}
                                                            height={15}
                                                            src={country.src}
                                                            alt={`Flag of ${country.label}`}
                                                            style={{
                                                                marginRight: 8,
                                                                marginTop: '3px',
                                                                [theme.breakpoints.down(600)]: {
                                                                    marginRight: 4
                                                                }
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: '900',
                                                                [theme.breakpoints.down(600)]: {
                                                                    fontSize: '12px',
                                                                    fontWeight: '700'
                                                                }
                                                            }}
                                                        >
                                                            ({country.country_code})
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl
                                            fullWidth
                                            error={Boolean(touched.phone && errors.phone)}
                                            sx={{
                                                ...theme.typography.customInput,
                                                width: '70%',
                                                [theme.breakpoints.down(600)]: {
                                                    width: '70%'
                                                }
                                            }}
                                        >
                                            {/* <InputLabel htmlFor="outlined-adornment-email-login">Mobile</InputLabel> */}

                                            <TextField
                                                id="outlined-adornment-phone-login"
                                                type="text"
                                                value={values.phone}
                                                name="phone"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                inputProps={{}}
                                                error={touched.phone && Boolean(errors.phone)}
                                                helperText={touched.phone && errors.phone}
                                                label="Mobile"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="login"
                                                                //onClick={handleClickChangeLoginMode}
                                                                onMouseDown={handleMouseDownPassword}
                                                                edge="end"
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: '600',
                                                                    fontFamily: '"Manrope", sans-serif',
                                                                    color: '#212121',
                                                                    ':hover': {
                                                                        borderColor: 'inherit',
                                                                        bgcolor: 'inherit'
                                                                    }
                                                                }}
                                                            >
                                                                {/* <IconChevronLeft /> <IconMail /> */}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                            {/* {touched.phone && errors.phone && (
                                <FormHelperText error id="standard-weight-helper-text-email-login">
                                    {errors.phone}
                                </FormHelperText>
                                )} */}
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                            {loginMode === email && (
                                <FormControl
                                    className=""
                                    fullWidth
                                    error={Boolean(touched.email && errors.email)}
                                    sx={{ ...theme.typography.customInput }}
                                >
                                    <TextField
                                        id="outlined-adornment-email-register"
                                        type="email"
                                        value={values.email}
                                        name="email"
                                        label="Email Address"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        inputProps={{}}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </FormControl>
                            )}
                            <FormControl
                                fullWidth
                                error={Boolean(touched.password && errors.password)}
                                sx={{ ...theme.typography.customInput }}
                            >
                                <TextField
                                    id="outlined-adornment-password-register"
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    name="password"
                                    label="Password"
                                    onBlur={handleBlur}
                                    error={touched.password && Boolean(errors.password)}
                                    helperText={touched.password && errors.password}
                                    onChange={(e) => {
                                        handleChange(e);
                                        changePassword(e.target.value);
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                    size="large"
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                {/* {touched.password && errors.password && (
                                <FormHelperText error id="standard-weight-helper-text-password-register">
                                    {errors.password}
                                </FormHelperText>
                            )} */}
                            </FormControl>

                            {values.password && strength !== 0 && (
                                <FormControl fullWidth className="454545">
                                    <Box sx={{ mb: 0 }}>
                                        <Grid container spacing={0} alignItems="center">
                                            <Grid item>
                                                <Box sx={{ width: 100, height: 8, borderRadius: '10px', bgcolor: level?.color }} />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="subtitle1" fontSize="0.75rem" sx={{ pl: '8px', fontWeight: '900' }}>
                                                    {level?.label}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </FormControl>
                            )}
                            <FormControl
                                fullWidth
                                error={Boolean(touched.passwordConfirmation && errors.passwordConfirmation)}
                                sx={{ ...theme.typography.customInput }}
                            >
                                {/* <InputLabel htmlFor="outlined-adornment-password-register">Confirm Password</InputLabel> */}
                                <TextField
                                    id="outlined-adornment-password-register"
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.passwordConfirmation}
                                    name="passwordConfirmation"
                                    label="Confirm Password"
                                    onBlur={handleBlur}
                                    error={touched.passwordConfirmation && Boolean(errors.passwordConfirmation)}
                                    helperText={touched.passwordConfirmation && errors.passwordConfirmation}
                                    onChange={(e) => {
                                        handleChange(e);
                                        // changePassword(e.target.value);
                                    }}
                                    inputProps={{}}
                                />
                                {/* {touched.passwordConfirmation && errors.passwordConfirmation && (
                                <FormHelperText error id="standard-weight-helper-text-password-register">
                                    {errors.passwordConfirmation}
                                </FormHelperText>
                            )} */}
                            </FormControl>
                            {CAPTCHA === 'true' && (
                                <FormControl
                                    fullWidth
                                    error={Boolean(touched.password && errors.password)}
                                    sx={{ ...theme.typography.customInput }}
                                >
                                    <ReCAPTCHA
                                        sitekey="6Lc_CWQqAAAAAHbsSmSBA9j-8p09voH1rLhXaxDl"
                                        ref={recaptchaRef}
                                        onChange={handleCaptcha}
                                    />

                                    <Typography sx={{ color: '#f44336' }} className="99999">
                                        {captchaMessage}
                                    </Typography>
                                </FormControl>
                            )}
                            {errors.submit && (
                                <Box sx={{ mt: 0 }}>
                                    <FormHelperText
                                        className="88888"
                                        sx={{
                                            fontSize: '.85rem',
                                            fontWeight: '600',
                                            textAlign: 'center',
                                            position: 'inherit !important',
                                            bottom: '0px !important',
                                            left: '0px !important'
                                        }}
                                        error
                                    >
                                        {errors.submit}
                                    </FormHelperText>
                                </Box>
                            )}

                            <Box sx={{ mt: 2 }}>
                                <AnimateButton>
                                    <Button
                                        sx={{
                                            // background: 'none',
                                            color:theme.palette.grey[500],
                                            border: '1px solid ',
                                            textTransform: 'none',
                                            borderColor: theme.palette.secondary.main,
                                            background: theme.palette.secondary.main,
                                            width: '100%',
                                            fontSize: '16px',
                                            fontWeight: '700',
                                            ':hover': {
                                                borderColor: theme.palette.secondary.main,
                                                background: theme.palette.secondary.main,
                                                color:theme.palette.grey[500],
                                            }
                                        }}
                                        color="secondary"
                                        disabled={isSubmitting}
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                    >
                                        Submit{isSubmitting && <SpinnerLoader />}
                                    </Button>
                                </AnimateButton>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Forgotpassword>
        </>
    );
};

export default ForgotPasswordForm;
